import { forwardRef, type AriaRole, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';
import { T } from '@territory/tokens';
import { mix as mixEdge } from '../internal/severity.js';
import {wedgeAuthAlertStyle, type WedgeAuthAlertLook} from '../internal/wedgeAuthAlert.js';
import { font, leading, radius, space, weight } from '../scale.js';
import { consoleRole, type ConsoleRole } from '../internal/consoleRoles.js';
import { IconButton } from '../IconButton/IconButton.js';
import { renderIconSlot, type IconSlot } from '../internal/iconSlot.js';

export type AlertTone = 'info' | 'success' | 'warning' | 'error' | 'neutral';

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'style' | 'title' | 'role'> {
  tone?: AlertTone;
  title?: ReactNode;
  /* Body copy. */
  children?: ReactNode;
  /* Defaults per tone; null hides the icon. */
  icon?: IconSlot | null;
  /* Trailing control, e.g. <Button size="sm" variant="ghost">. */
  action?: ReactNode;
  /* Renders a "Dismiss" close button. */
  onDismiss?: () => void;
  /* Announce on mount: role="alert" for error, role="status" otherwise. Defaults to true for error only. */
  live?: boolean;
  /* 'console': the owner console status bar (legacy .mtbar): 13px on a 1.5 line, radius 11, 10px 12px pad, a 15px
     icon in the tone hue, the title inline and bold before the body, the action at the top right. Tone fill is the
     soft hue (neutral: the surface), edge the hue at 35 to 40 percent (neutral: the separator), ink the secondary label. */
  variant?: 'default' | 'console' | 'wedge-auth';
  /* variant="console" only. 'panel': the admin weekly digest box (legacy .digestbox): a neutral surface box, no icon, no tone
     edge, 12px type, wrapping row with 10px gap, radius 12, pad 11px 14px; the title bold, the body, then the action slot at
     the right (put core Buttons there). Tone is ignored. 'status' (default) is the .mtbar bar. */
  consoleLook?: 'status' | 'panel' | 'banner' | 'advisory';
  /* variant="console" consoleLook="advisory": the admin editor error line (legacy .advisory, the quota editor's #qErr): an amber
     box with no icon, title or action: radius 10, 10px by 13px of pad, 12px mono on a 1.6 line, 12px above, the amber ink on the
     soft amber fill, and a fixed amber edge at 40 percent. `tone` is ignored; `live` announces it as an alert. */
  /* variant="console" consoleLook="banner": the admin Rep page notice (legacy .adv-ban, onboarding still open): a 14px amber
     gradient band with an amber edge, a 34px rounded icon tile (`icon`, drawn at 15px in the amber ink), a column of an
     amber title over a secondary-ink body, then the action. Always an amber status; `tone` is ignored. */
  /* Wedge native looks only. The caller supplies direct children and product placement. */
  authLook?: WedgeAuthAlertLook;
  /* Explicit native role for wedge-auth only. Existing variants retain their live policy. */
  role?: AriaRole;
  className?: string;
  style?: CSSProperties;
  'data-testid'?: string;
}

const ICON = 18;
/* Centers the 18px glyph on the first 14px x 1.45 text line. */
const ICON_NUDGE = (font.body * leading.body - ICON) / 2;
/* The 44px dismiss hit area overhangs the padding so a dismissible alert keeps its compact height. */
const DISMISS_OVERHANG = -space[3];

const TONES: Record<AlertTone, { bg: string; ink: string; icon: IconSlot }> = {
  info: { bg: T.blueSoft, ink: T.blueInk, icon: 'info' },
  success: { bg: T.accentSoft, ink: T.accentInk, icon: 'check' },
  warning: { bg: T.amberSoft, ink: T.amberInk, icon: 'alert' },
  error: { bg: T.redSoft, ink: T.red, icon: 'alert' },
  neutral: { bg: T.surface, ink: T.labelTer, icon: 'info' },
};

/* Legacy .mtbar, verbatim, off-scale. Edge mixes: .on and .cy 35%, .warn and .red 40%. */
const CONSOLE_ICON = 15;
const CONSOLE_GAP = 10;
const CONSOLE_PAD = '10px 12px';
const CONSOLE_RADIUS = 11;
const CONSOLE_LEADING = 1.5;
const CONSOLE_ICON_TOP = 2;
/* Legacy .digestbox, verbatim, off-scale. */
const PANEL_GAP = 10;
const PANEL_PAD = '11px 14px';
const PANEL_RADIUS = 12;
/* Legacy .advisory, verbatim, off-scale. The edge is the fixed brand amber at 40% in both themes. */
const ADVISORY_PAD = '10px 13px';
const ADVISORY_RADIUS = 10;
const ADVISORY_TOP = 12;
const ADVISORY_SIZE = 12;
const ADVISORY_LEADING = 1.6;
const ADVISORY_EDGE_MIX = 40;
/* Legacy .adv-ban, verbatim, off-scale. */
const BANNER_GAP = 14;
const BANNER_PAD = '13px 14px 13px 16px';
const BANNER_RADIUS = 14;
const BANNER_ICON_BOX = 34;
const BANNER_ICON_RADIUS = 10;
const BANNER_ICON = 15;
const BANNER_TEXT_MIN = 200;
const BANNER_TEXT_GAP = 2;
const BANNER_TITLE = 14.5;
const BANNER_EDGE_MIX = 45;
const BANNER_FADE_MIX = 5;
const CONSOLE_TONES: Record<AlertTone, { bg: string; edge: string; icon: string }> = {
  info: { bg: T.blueSoft, edge: mixEdge(T.blue, 35), icon: T.blue },
  success: { bg: T.accentSoft, edge: mixEdge(T.accent, 35), icon: T.accent },
  warning: { bg: T.amberSoft, edge: mixEdge(T.amber, 40), icon: T.amber },
  error: { bg: T.redSoft, edge: mixEdge(T.red, 40), icon: T.red },
  neutral: { bg: T.surface, edge: T.separator, icon: T.labelTer },
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { tone = 'info', title, children, icon, action, onDismiss, live, variant = 'default', consoleLook = 'status', authLook = 'field-error', role, className, style, ...rest },
  ref,
) {
  if (variant === 'wedge-auth') {
    return (
      <div
        {...rest}
        ref={ref}
        data-rv-alert=""
        data-rv-variant="wedge-auth"
        data-rv-auth-look={authLook}
        role={role}
        className={className}
        style={{...wedgeAuthAlertStyle(authLook), ...style}}
      >
        {children}
      </div>
    );
  }
  const t = TONES[tone];
  const announce = live ?? tone === 'error';
  if (variant === 'console' && consoleLook === 'panel') {
    return (
      <div
        {...rest}
        ref={ref}
        data-rv-alert=""
        data-rv-variant="console"
        data-rv-console-look="panel"
        className={className}
        style={{
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: PANEL_GAP,
          padding: PANEL_PAD,
          borderRadius: PANEL_RADIUS,
          border: `1px solid ${T.separator}`,
          background: T.surface,
          color: T.label,
          fontSize: font.caption,
          ...style,
        }}
      >
        {title ? <b>{title}</b> : null}
        {children}
        {action ? (
          <>
            <span style={{ flex: '1 1 auto' }} />
            {action}
          </>
        ) : null}
      </div>
    );
  }
  if (variant === 'console' && consoleLook === 'advisory') {
    return (
      <div
        {...rest}
        ref={ref}
        data-rv-alert=""
        data-rv-variant="console"
        data-rv-console-look="advisory"
        role={live ? 'alert' : undefined}
        className={className}
        style={{
          boxSizing: 'border-box',
          marginTop: ADVISORY_TOP,
          padding: ADVISORY_PAD,
          borderRadius: ADVISORY_RADIUS,
          border: `1px solid ${mixEdge(T.brandAmber, ADVISORY_EDGE_MIX)}`,
          background: T.amberSoft,
          color: T.amber,
          fontFamily: T.mono,
          fontSize: ADVISORY_SIZE,
          lineHeight: ADVISORY_LEADING,
          ...style,
        }}
      >
        {children}
      </div>
    );
  }
  if (variant === 'console' && consoleLook === 'banner') {
    const edge = mixEdge(T.brandAmber, BANNER_EDGE_MIX);
    /* The admin amber chip ink (legacy --amber): lighter than the soft-tone ink in dark, deep rust in light. */
    const ink = consoleRole('chip-amber-ink' as ConsoleRole, T.amberInk);
    const bannerGlyph = icon === null ? null : renderIconSlot(icon === undefined ? 'bolt' : icon, BANNER_ICON);
    return (
      <div
        {...rest}
        ref={ref}
        data-rv-alert=""
        data-rv-variant="console"
        data-rv-console-look="banner"
        role="status"
        className={className}
        style={{
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: BANNER_GAP,
          padding: BANNER_PAD,
          borderRadius: BANNER_RADIUS,
          border: `1px solid ${edge}`,
          background: `linear-gradient(100deg, ${T.amberSoft}, ${mixEdge(T.brandAmber, BANNER_FADE_MIX)})`,
          ...style,
        }}
      >
        {bannerGlyph ? (
          <span
            data-rv-alert-icon=""
            aria-hidden="true"
            style={{
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              width: BANNER_ICON_BOX,
              height: BANNER_ICON_BOX,
              borderRadius: BANNER_ICON_RADIUS,
              color: ink,
              background: T.amberSoft,
              border: `1px solid ${edge}`,
            }}
          >
            {bannerGlyph}
          </span>
        ) : null}
        <div style={{ flex: '1 1 0%', minWidth: BANNER_TEXT_MIN, display: 'flex', flexDirection: 'column', gap: BANNER_TEXT_GAP }}>
          {title ? <div style={{ fontSize: BANNER_TITLE, fontWeight: weight.bold, color: ink }}>{title}</div> : null}
          {children ? <div style={{ fontSize: font.footnote, color: T.labelSec }}>{children}</div> : null}
        </div>
        {action}
      </div>
    );
  }
  if (variant === 'console') {
    const c = CONSOLE_TONES[tone];
    const cglyph = icon === null ? null : renderIconSlot(icon === undefined ? t.icon : icon, CONSOLE_ICON);
    return (
      <div
        {...rest}
        ref={ref}
        data-rv-alert=""
        data-rv-tone={tone}
        data-rv-variant="console"
        role={announce ? (tone === 'error' ? 'alert' : 'status') : undefined}
        className={className}
        style={{
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'flex-start',
          gap: CONSOLE_GAP,
          padding: CONSOLE_PAD,
          borderRadius: CONSOLE_RADIUS,
          border: `1px solid ${c.edge}`,
          background: c.bg,
          color: T.labelSec,
          fontSize: font.footnote,
          lineHeight: CONSOLE_LEADING,
          ...style,
        }}
      >
        {cglyph ? (
          <span data-rv-alert-icon="" aria-hidden="true" style={{ display: 'flex', flex: `0 0 ${CONSOLE_ICON}px`, color: c.icon, marginTop: CONSOLE_ICON_TOP }}>
            {cglyph}
          </span>
        ) : null}
        <span style={{ minWidth: 0 }}>
          {title ? <b style={{ color: T.label }}>{title}</b> : null}
          {title && children ? ' ' : null}
          {children}
        </span>
        {action ? (
          <>
            <span style={{ flex: '1 1 auto' }} />
            {action}
          </>
        ) : null}
      </div>
    );
  }
  const glyph = icon === null ? null : renderIconSlot(icon === undefined ? t.icon : icon, ICON);

  return (
    <div
      {...rest}
      ref={ref}
      data-rv-alert=""
      data-rv-tone={tone}
      role={announce ? (tone === 'error' ? 'alert' : 'status') : undefined}
      className={className}
      style={{
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'flex-start',
        gap: space[3],
        padding: `${space[3]}px ${space[4]}px`,
        borderRadius: radius.md,
        border: `1px solid ${T.separator}`,
        background: t.bg,
        color: T.label,
        fontFamily: T.font,
        fontSize: font.body,
        lineHeight: leading.body,
        ...style,
      }}
    >
      {glyph ? (
        <span
          data-rv-alert-icon=""
          aria-hidden="true"
          style={{ display: 'flex', flexShrink: 0, color: t.ink, marginTop: ICON_NUDGE }}
        >
          {glyph}
        </span>
      ) : null}
      <div style={{ flex: 1, minWidth: 0 }}>
        {title ? <div style={{ fontWeight: weight.semibold }}>{title}</div> : null}
        {children ? <div data-rv-alert-body="">{children}</div> : null}
      </div>
      {action ? <div style={{ flexShrink: 0, alignSelf: 'center' }}>{action}</div> : null}
      {onDismiss ? (
        <IconButton
          icon="close"
          aria-label="Dismiss"
          size="sm"
          variant="plain"
          onClick={onDismiss}
          style={{ margin: `${DISMISS_OVERHANG}px ${DISMISS_OVERHANG}px ${DISMISS_OVERHANG}px 0` }}
        />
      ) : null}
    </div>
  );
});
